import Button from '@atlaskit/button/new';
import PageHeader from '@atlaskit/page-header';
import { Box, xcss } from '@atlaskit/primitives';
import Spinner from '@atlaskit/spinner';
import { GlobalPageLoadExperience } from '@atlassian/ufo';
import { DatabaseContainerType, useAnalytics } from '@databases/analytics';
import { useLocale } from '@databases/intl';
import { messages } from '@databases/intl/messages';
import ResizeObserver from '@databases/layout/observer/ResizeObserver';
import { logError } from '@databases/logs';
import { useCallbackEffect } from '@databases/react';
import { appStoreUtils } from '@databases/redux/store';
import { AsyncOperationStatus } from '@databases/redux-types';
import {
  getDatabaseEntryOptional,
  getEntryName,
  isBrokenDatabase,
} from '@databases/utils/database/utils';
import { loadDatabaseByContentId } from '@databases/utils/redux/databases/actions';
import {
  selectDatabaseByContentIdOptional,
  selectDatabaseByContentIdStatus,
} from '@databases/utils/redux/databases/selectors';
import { deserializeError } from '@databases/utils/utils';
import React, { lazy, type FunctionComponent, Suspense, useCallback } from 'react';
import { useIntl } from 'react-intl-next';

import { dynamicImport } from '@canvas/dynamic-import';

import genericErrorIllustration from '../../../../assets/generic_error.svg';
import DatabaseEntry from '../../../../common/components/database-entry/DatabaseEntry';
import DatabaseEntrySkeleton from '../../../../common/components/database-entry/DatabaseEntrySkeleton';
import ImmediateEntryActions, {
  type ImmediateEntryActionKey,
} from '../../../../common/components/database-entry/ImmediateEntryActions';
import { ButtonGroup } from '../../../../common/components/styled';

const ShrinkingEmptyState = lazy(() =>
  dynamicImport(() => import('../../../../common/components/ShrinkingEmptyState'))
);

const EmbedEmptyState = lazy(() => dynamicImport(() => import('./EmbedEmptyState')));

const entryActions: ImmediateEntryActionKey[] = ['print'];

const EmbedEntryDetails: FunctionComponent<{
  contentId: string;
  entryId: string;
  isContentEditable?: boolean;
  onDataLoaded?: () => void;
}> = ({ contentId, entryId, isContentEditable = true, onDataLoaded }) => {
  const [databaseExists, entryExists, title] = appStoreUtils.useEqualSelector((state) => {
    const database = appStoreUtils.useSelector((state) =>
      selectDatabaseByContentIdOptional(state, { contentId })
    );
    const entry =
      database &&
      !isBrokenDatabase(database) &&
      getDatabaseEntryOptional(database, entryId);
    return [
      !!database,
      !!entry,
      database && entry ? getEntryName({ database, entry }, state) : undefined,
    ] as const;
  });

  const intl = useIntl();
  const { formatMessage } = intl;

  const dispatch = appStoreUtils.useDispatch();
  useCallbackEffect(
    useCallback(async () => {
      GlobalPageLoadExperience.mark('database-load-start');
      await dispatch(loadDatabaseByContentId(contentId));
    }, [contentId, dispatch])
  );
  const databaseStatus = appStoreUtils.useSelector((state) =>
    selectDatabaseByContentIdStatus(state, { contentId })
  );

  const client = useAnalytics();
  const locale = useLocale();

  useCallbackEffect(
    useCallback(async () => {
      await client.sendDatabaseContentViewedEvent({
        contentId,
        databaseContainer: DatabaseContainerType.EMBED_ENTRY,
        locale,
      });
    }, [client, contentId, locale])
  );

  switch (databaseStatus?.status) {
    case AsyncOperationStatus.ERROR: {
      const error = deserializeError(databaseStatus.error);
      const formattedError = {
        name: error.name,
        message: error.message,
        stack: error.stack,
      };
      GlobalPageLoadExperience.failure({
        metadata: { error: formattedError },
        force: true,
      });
      logError(new Error(`Couldn't load link with error: ${error}`));
      return (
        <Suspense fallback={<Spinner interactionName="load" />}>
          <ShrinkingEmptyState
            header={formatMessage(messages['embed-view.error-heading-text'])}
            description={formatMessage(messages['embed-view.error-description-text'])}
            imageUrl={genericErrorIllustration}
            primaryAction={
              <Button appearance="primary" onClick={() => globalThis.location.reload()}>
                {formatMessage(messages['embed-view.try-again-button-text'])}
              </Button>
            }
            maxImageWidth={300}
            maxImageHeight={250}
          />
        </Suspense>
      );
    }
    case AsyncOperationStatus.SUCCEEDED:
      GlobalPageLoadExperience.mark('database-load-end');
      GlobalPageLoadExperience.success({ force: true });
      onDataLoaded?.();
      break;
    default:
      return <DatabaseEntrySkeleton />;
  }

  if (!databaseExists) {
    return (
      <Suspense fallback={<Spinner interactionName="load" />}>
        <EmbedEmptyState />
      </Suspense>
    );
  }

  if (!entryExists) {
    return (
      <Suspense fallback={<Spinner interactionName="load" />}>
        <EmbedEmptyState />
      </Suspense>
    );
  }

  return (
    <>
      <Box id="layout-toolbar-wrapper">
        <PageHeader
          id="embed-entry-details-entry-name"
          actions={
            <ButtonGroup>
              <ImmediateEntryActions
                contentId={contentId}
                entryId={entryId}
                placement="bottom-end"
                show={entryActions}
              />
            </ButtonGroup>
          }
        >
          {title ? title : formatMessage(messages['unnamed-entities.unnamed-entry'])}
        </PageHeader>
      </Box>

      <Box xcss={xcss({ overflowY: 'auto', height: '100%' })}>
        <ResizeObserver>
          <DatabaseEntry
            contentId={contentId}
            entryId={entryId}
            isContentEditable={isContentEditable}
            isEmbedEntryView
          />
        </ResizeObserver>
      </Box>
    </>
  );
};
export default EmbedEntryDetails;
